// src/components/Sidebar.jsx
import React from "react";
import { Link } from "react-router-dom";
import { useArticlesContext } from "../context/ArticlesContext";

/**
 * Fisher-Yates shuffle for in-place array shuffling.
 */
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

const Sidebar = ({ currentArticle }) => {
  const { articles, labels } = useArticlesContext();

  // We'll collect up to 6 articles to display.
  let bestMatches = [];

  // Check if we have a current article with valid labels:
  if (currentArticle && currentArticle.labels && currentArticle.labels.length > 0) {
    const refLabels = currentArticle.labels;

    // Separate out the other articles (excluding the current one).
    const otherArticles = articles.filter((art) => art.slug !== currentArticle.slug);

    // 1) Compute overlap count for each other article
    const scored = otherArticles.map((art) => {
      const overlapCount = art.labels
        ? art.labels.filter((lblId) => refLabels.includes(lblId)).length
        : 0;
      return { ...art, overlapCount };
    });

    // 2) Sort by overlapCount descending
    scored.sort((a, b) => b.overlapCount - a.overlapCount);

    // 3) Group by overlapCount so we can randomize within each group
    //    e.g. { 3: [...], 2: [...], 1: [...], 0: [...] }
    const groupedByOverlap = {};
    scored.forEach((item) => {
      const oc = item.overlapCount;
      if (!groupedByOverlap[oc]) {
        groupedByOverlap[oc] = [];
      }
      groupedByOverlap[oc].push(item);
    });

    // We'll build a final list in descending overlap order,
    // but shuffle each group that has the same overlap.
    const sortedOverlaps = Object.keys(groupedByOverlap)
      .map(Number)
      .sort((a, b) => b - a); // descending numeric sort

    let finalList = [];
    for (let oc of sortedOverlaps) {
      // shuffle the group
      shuffleArray(groupedByOverlap[oc]);
      finalList = finalList.concat(groupedByOverlap[oc]);
    }

    // Now we have a single array in descending overlap, but within
    // each overlap group the order is random. Let's pick the first 6.
    bestMatches = finalList.slice(0, 6);
  }

  // If we got no matches, or fewer than 6 from overlap logic, fallback:
  if (bestMatches.length < 6) {
    // We can fill up or replace with random articles
    let randomArticles = articles.filter(
      (art) => !currentArticle || art.slug !== currentArticle.slug
    );

    shuffleArray(randomArticles);

    // If we have some bestMatches from overlap, keep them;
    // but fill the rest with random ones (avoid duplicates).
    const needed = 6 - bestMatches.length;
    const fallback = [];

    for (let i = 0; i < randomArticles.length && fallback.length < needed; i++) {
      // ensure we don't add duplicates
      if (!bestMatches.some((b) => b.slug === randomArticles[i].slug)) {
        fallback.push(randomArticles[i]);
      }
    }

    bestMatches = bestMatches.concat(fallback);
  }

  // If there's still no articles at all, bestMatches might be an empty array.
  // But if we have exactly 6, let's shuffle the final 6 to randomize display order
  if (bestMatches.length > 0) {
    bestMatches = bestMatches.slice(0, 6); // ensure not more than 6
    shuffleArray(bestMatches); // final shuffle for random order in sidebar
  }

  // Show only the labels that belong to the current article
  const articleLabels = currentArticle?.labels
    ? labels.filter((lbl) => currentArticle.labels.includes(lbl.id))
    : [];

  // ------------- transform bestMatches for low-res fallback -------------
  const processedMatches = bestMatches.map((art) => {
    const copy = { ...art };
    copy.imageOriginal = copy.image;
    if (copy.image && copy.image.endsWith(".jpg")) {
      copy.image = copy.image.replace(".jpg", "-low.jpg");
    }
    return copy;
  });

  return (
    <aside className="sidebar" aria-label="Sidebar with popular posts and categories">
      <div className="widget popular-posts-widget">
        <h3>Popular Posts</h3>
        {processedMatches.length > 0 ? (
          <ul className="popular-posts-list">
            {processedMatches.map((article) => (
              <li key={article.slug} className="popular-post-item">
                <Link to={`/articles/${article.slug}`} className="popular-post-card">
                  <div className="popular-post-image">
                    <img
                      src={article.image}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = article.imageOriginal;
                      }}
                      alt={article.title}
                    />
                  </div>
                  <div className="popular-post-content">
                    <h4>{article.title}</h4>
                    <p>{article.excerpt}</p>
                    <span className="read-more">Read More &rarr;</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p>No posts available.</p>
        )}
      </div>

      <div className="widget categories-widget">
        <h3>Categories</h3>
        <ul className="categories-list">
          {articleLabels.length > 0 ? (
            articleLabels.map((label) => (
              <li key={label.id}>
                {/* CHANGE: link to /articles with query params */}
                <Link
                  to={`/articles?category=${label.category}&labels=${label.id}`}
                  aria-label={`Category: ${label.name}`}
                >
                  {label.name}
                </Link>
              </li>
            ))
          ) : (
            <li>
              <em>No categories for this article.</em>
            </li>
          )}
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;